<template>
  <dl :class="$style.profile">
    <div
      v-for="item in userDetailedProfileItems"
      :key="item.name"
      :class="$style.item"
    >
      <dt :class="$style.item__title">{{ item.name }}</dt>
      <dd :class="[$style.item__detail, $style.detail]">
        <span :class="$style.detail__data">{{ item.value }} </span>
      </dd>
    </div>

    <div :class="$style.item">
      <dt :class="$style.item__title">子供</dt>
      <dd :class="[$style.item__detail, $style.detail]">
        <span
          v-if="props.diagnosis.children.length === 0"
          :class="$style.detail__data"
          >なし</span
        >
        <ul :class="$style.children" v-else>
          <li
            v-for="(child, index) in props.diagnosis.children"
            :key="index"
            :class="[$style.child, $style[childClassName(child)]]"
          >
            {{ childAgeString(child.age) }}歳
          </li>
        </ul>
      </dd>
    </div>
  </dl>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import formatToJpy from '@/libs/utilities/formatToJpy'
import { sum } from '@/libs/utilities/array'
import { childAgeString } from '@/libs/utilities/child'

import type {
  KarteUserDetailedProfileDiagnosisFragment,
  KarteUserDetailedProfileChildFragment,
} from './KarteUserDetailedProfile.generated'
import { Gender } from '@/types/schema.generated'

const stringifyPartnerExistence = (value: boolean): 'あり' | 'なし' =>
  value ? 'あり' : 'なし'

const props = defineProps<{
  diagnosis: KarteUserDetailedProfileDiagnosisFragment
}>()

type UserDetailedProfileItem = { name: string; value: string }
const userDetailedProfileItems = computed((): UserDetailedProfileItem[] => {
  return [
    {
      name: '世帯年収',
      value: formatToJpy(
        sum([
          props.diagnosis.annualIncome,
          props.diagnosis.partner?.annualIncome ?? 0,
        ])
      ),
    },
    { name: '預貯金', value: formatToJpy(props.diagnosis.saving) },
    {
      name: '毎月の保険料',
      value: formatToJpy(props.diagnosis.monthlyInsurance),
    },
    {
      name: '資産運用合計',
      value: formatToJpy(props.diagnosis.investment ?? 0),
    },
    {
      name: '配偶者',
      value: stringifyPartnerExistence(!!props.diagnosis.partner),
    },
  ]
})
const childClassName = (child: KarteUserDetailedProfileChildFragment) =>
  child.gender === Gender.Male ? 'child--male' : 'child--female'
</script>

<style lang="scss" module>
.profile {
  display: grid;
  gap: 2px;
  grid-template: auto / repeat(3, 1fr);
}

.item {
  display: grid;
  grid-template: 19px minmax(19px, auto) / 1fr;
  text-align: center;

  &__title {
    color: $color-text-and-object-high-emphasis;
    font-size: 12px;
    font-weight: bold;
    line-height: 20px;
    vertical-align: middle;
  }

  &__detail {
    margin-left: 0;
  }
}

.detail {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  color: $color-text-and-object-high-emphasis;
  font-size: 12px;

  &__data {
    line-height: 20px;
  }
}

.children {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0 2px;
}

.child {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:nth-last-child(n + 2) {
    &::after {
      color: $color-text-and-object-low-emphasis;
      content: ',';
    }
  }

  &--male {
    color: $color-text-and-object-accent-primary;
  }

  &--female {
    color: $color-text-and-object-accent-red;
  }
}
</style>
